/**
 * CRC Evans design-system sidebar (docs/crce_design_system.md — Sidebar template).
 * Role gates match legacy layout/sidebar.js for parity with ModernLayout routes.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { notification } from 'antd';
import { FolderOpen, Briefcase, Wrench, ClipboardCheck, Headphones, LogOut } from 'lucide-react';
import { isTechnicalNavPath } from '../../pages/technical/technicalTabItems';
import { signOut } from '../../redux/actions/authActions';
import { finalizeClientLogout } from '../../services/auth';
import logoCrc from '../../assets/logoCrc.png';
import redHexagon from '../../assets/red-Hexagon.png';
import './ModernSidebar.css';

const EMPTY_USER_ACCESS_LIST = [];

const readUserGroupsFromStorage = () => {
  try {
    const raw = localStorage.getItem('UserGroup');
    if (raw == null || raw === '') return EMPTY_USER_ACCESS_LIST;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : EMPTY_USER_ACCESS_LIST;
  } catch {
    return EMPTY_USER_ACCESS_LIST;
  }
};

/** Role visibility aligned with `src/layout/sidebar.js` */
function buildNavVisibility(userGroup) {
  const g = Array.isArray(userGroup) ? userGroup : [];
  const hasProjects =
    g.includes('web-page') ||
    g.includes('web-page-without-create-project') ||
    g.includes('super-admin') ||
    g.includes('admin');
  const hasTechnical =
    g.includes('heat-profile') ||
    g.includes('coating-record-db') ||
    g.includes('dcal-generator') ||
    g.includes('super-admin') ||
    g.includes('admin');
  const hasCalibration =
    g.includes('calibration-normal-user') || g.includes('super-admin') || g.includes('admin');
  const hasAdmin = g.includes('admin') || g.includes('super-admin');
  const hasAskEvans =
    g.includes('askevans-normal-user') || g.includes('super-admin') || g.includes('admin');
  return { hasProjects, hasTechnical, hasCalibration, hasAdmin, hasAskEvans };
}

function roleLabelFromGroups(userGroup) {
  const g = Array.isArray(userGroup) ? userGroup : [];
  if (g.includes('super-admin')) return 'Super Admin';
  if (g.includes('admin')) return 'Administrator';
  if (g.includes('analysis')) return 'Analysis';
  if (g.includes('web-page') || g.includes('web-page-without-create-project')) return 'User';
  return 'User';
}

const ModernSidebar = (props) => {
  const history = useHistory();
  const location = useLocation();
  const { userAccessList, onSignout } = props;

  const calibrationPaths = ['/calibration', '/calibration-control'];

  const [activeId, setActiveId] = useState(() => {
    const path = window.location.pathname || '';
    if (isTechnicalNavPath(path)) return 'technical';
    if (calibrationPaths.some((p) => path === p || path.startsWith(`${p}/`))) return 'calibration';
    if (path === '/AskEvans' || path.startsWith('/AskEvans/')) return 'askevans';
    if (
      path === '/projects' ||
      path.startsWith('/projects/') ||
      path === '/dashboard' ||
      path.startsWith('/dashboard/') ||
      path === '/dpr' ||
      path.startsWith('/dpr/')
    ) {
      return 'projects';
    }
    if (path === '/admin' || path.startsWith('/admin/')) return 'admin';
    return 'projects';
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userGroup = useMemo(() => {
    const fromRedux = Array.isArray(userAccessList) ? userAccessList : [];
    if (fromRedux.length > 0) return fromRedux;
    return readUserGroupsFromStorage();
  }, [userAccessList]);

  const visibility = useMemo(() => buildNavVisibility(userGroup), [userGroup]);

  const defaultUser = useMemo(() => {
    const name =
      user['custom:first_name'] && user['custom:last_name']
        ? `${user['custom:first_name']} ${user['custom:last_name']}`
        : user['custom:first_name'] || user.email || 'User';
    const initialsRaw = `${user['custom:first_name']?.charAt(0) || ''}${
      user['custom:last_name']?.charAt(0) || ''
    }`.toUpperCase();
    const initials = initialsRaw || (user.email ? user.email.charAt(0).toUpperCase() : 'U');
    return {
      name,
      role: roleLabelFromGroups(userGroup),
      initials,
    };
  }, [user, userGroup]);

  const navItems = useMemo(() => {
    const items = [];
    if (visibility.hasProjects) {
      items.push({ id: 'projects', label: 'Projects', icon: FolderOpen });
    }
    if (visibility.hasTechnical) {
      items.push({ id: 'technical', label: 'Technical', icon: Wrench });
    }
    if (visibility.hasCalibration) {
      items.push({ id: 'calibration', label: 'Calibration & Control', icon: ClipboardCheck });
    }
    if (visibility.hasAdmin) {
      items.push({ id: 'admin', label: 'Admin', icon: Briefcase });
    }
    if (visibility.hasAskEvans) {
      items.push({ id: 'askevans', label: 'Ask Evans', icon: Headphones });
    }
    return items;
  }, [visibility]);

  useEffect(() => {
    const path = location.pathname || '';
    if (isTechnicalNavPath(path)) {
      setActiveId('technical');
    } else if (calibrationPaths.some((p) => path === p || path.startsWith(`${p}/`))) {
      setActiveId('calibration');
    } else if (
      path === '/projects' ||
      path.startsWith('/projects/') ||
      path === '/dashboard' ||
      path.startsWith('/dashboard/') ||
      path === '/dpr' ||
      path.startsWith('/dpr/')
    ) {
      setActiveId('projects');
    } else if (path === '/admin' || path.startsWith('/admin/')) {
      setActiveId('admin');
    } else if (path === '/AskEvans' || path.startsWith('/AskEvans/')) {
      setActiveId('askevans');
    } else {
      setActiveId('projects');
    }
  }, [location.pathname]);

  const handleItemClick = (itemId) => {
    if (itemId === 'projects') {
      setActiveId('projects');
      history.push('/projects');
    } else if (itemId === 'technical') {
      setActiveId('technical');
      history.push('/heat-profile');
    } else if (itemId === 'calibration') {
      setActiveId('calibration');
      history.push('/calibration');
    } else if (itemId === 'askevans') {
      setActiveId('askevans');
      history.push('/AskEvans');
    } else if (itemId === 'admin') {
      setActiveId('admin');
      history.push('/admin/asset-management');
    }
  };

  const handleLogout = () => {
    onSignout();
    notification.destroy();
    finalizeClientLogout();
    history.push('/login');
    notification.success({
      duration: 3,
      message: 'Successfully Logged Out',
      description: '',
    });
  };

  return (
    <aside className="crce-ds-sidebar" aria-label="Main navigation">
      <div className="crce-ds-sidebar__brand">
        <img src={redHexagon} alt="" className="crce-ds-sidebar__brand-mark" width={40} height={40} />
        <div className="crce-ds-sidebar__brand-text">
          <div className="crce-ds-sidebar__brand-title">CRCE Connect</div>
        </div>
      </div>

      <nav className="crce-ds-sidebar__nav" aria-label="Primary">
        {navItems.map(({ id, label, icon: Icon }) => {
          const isActive = activeId === id;
          return (
            <button
              key={id}
              type="button"
              className={
                isActive ? 'crce-ds-sidebar__nav-item crce-ds-sidebar__nav-item--active' : 'crce-ds-sidebar__nav-item'
              }
              onClick={() => handleItemClick(id)}
            >
              <Icon className="crce-ds-sidebar__nav-icon" strokeWidth={2} aria-hidden />
              <span>{label}</span>
            </button>
          );
        })}
      </nav>

      <div className="crce-ds-sidebar__footer">
        <div className="crce-ds-sidebar__profile">
          <div className="crce-ds-sidebar__avatar" aria-hidden>
            <span>{defaultUser.initials}</span>
          </div>
          <div className="crce-ds-sidebar__profile-text">
            <div className="crce-ds-sidebar__profile-name">{defaultUser.name}</div>
            <div className="crce-ds-sidebar__profile-role">{defaultUser.role}</div>
          </div>
        </div>

        <button type="button" className="crce-ds-sidebar__logout" onClick={handleLogout}>
          <LogOut className="crce-ds-sidebar__logout-icon" strokeWidth={2} aria-hidden />
          <span>Logout</span>
        </button>

        <div className="crce-ds-sidebar__powered">
          <p className="crce-ds-sidebar__powered-label">Powered by</p>
          <img src={logoCrc} alt="CRC Evans" className="crce-ds-sidebar__powered-logo" />
        </div>
      </div>
    </aside>
  );
};

const mapStateToProps = ({ auth, equip }) => {
  const list = get(equip, 'userAccessList');
  return {
    userAccess: auth?.userAccess || [],
    userAccessList: Array.isArray(list) ? list : EMPTY_USER_ACCESS_LIST,
  };
};

const mapDispatchToProps = {
  onSignout: signOut,
};

export default connect(mapStateToProps, mapDispatchToProps)(ModernSidebar);
